import React from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../types';
import { PLACEHOLDER_IMG } from '../utils/constants';
import { useWishlist } from '../contexts/WishlistContext';
import { useCart } from '../contexts/CartContext';
import './ProductCard.css';

interface ProductCardProps {
  product: Product;
  dark?:   boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, dark = false }) => {
  const { isWishlisted, toggleWishlist } = useWishlist();
  const { isInCart, addToCart } = useCart();
  const wishlisted = isWishlisted(product.id);
  const inCart = isInCart(product.id);

  const discount = product.mrp > product.price
    ? Math.round((1 - product.price / product.mrp) * 100)
    : 0;

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>): void => {
    e.currentTarget.src = PLACEHOLDER_IMG;
  };

  // Cards live inside a <Link> to the product page — stop the click from
  // navigating when the user is actually toggling wishlist/basket state.
  const handleWishlistClick = (e: React.MouseEvent): void => {
    e.preventDefault(); e.stopPropagation();
    toggleWishlist(product.id);
  };
  const handleCartClick = (e: React.MouseEvent): void => {
    e.preventDefault(); e.stopPropagation();
    if (!inCart) addToCart(product.id);
  };

  return (
    <Link to={`/product/${product.id}`} className={`product-card ${dark ? 'dark' : ''}`}>
      <div className="card-img-wrap">
        <img src={product.image} alt={product.name} loading="lazy" onError={handleImgError} />
        {discount > 0 && <span className="discount-badge">{discount}% OFF</span>}
        {product.quantity === 0 && <div className="out-of-stock-overlay">Out of Stock</div>}

        <button
          type="button"
          className={`card-icon-btn wishlist-btn ${wishlisted ? 'active' : ''}`}
          onClick={handleWishlistClick}
          aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          aria-pressed={wishlisted}
        >
          <i className={wishlisted ? 'fa-solid fa-heart' : 'fa-regular fa-heart'} aria-hidden="true" />
        </button>

        {product.quantity > 0 && (
          <button
            type="button"
            className={`card-icon-btn basket-btn ${inCart ? 'active' : ''}`}
            onClick={handleCartClick}
            aria-label={inCart ? 'Already in basket' : 'Add to basket'}
          >
            <i className={inCart ? 'fa-solid fa-check' : 'fa-solid fa-basket-shopping'} aria-hidden="true" />
          </button>
        )}

        <div className="card-overlay" aria-hidden="true">
          <span className="quick-view-btn">Quick View</span>
        </div>
      </div>
      <div className="card-body">
        <span className="badge badge-category">{product.category}</span>
        <p className="card-name">{product.name}</p>
        <div className="card-price">
          <span className="price-sale">₹{product.price.toLocaleString('en-IN')}</span>
          {product.mrp > product.price && (
            <span className="price-mrp">₹{product.mrp.toLocaleString('en-IN')}</span>
          )}
          {discount > 0 && <span className="price-discount">{discount}% off</span>}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
